import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Widget from 'components/dashboard/widget';
import WidgetLoadingSkeleton from 'components/dashboard/widgetLoadingSkeleton';

import { getToken } from 'context/auth';
import { useMessages } from 'context/messages';

import ParkingSpotsPriceTable from "../../components/pageSpecific/parkingSpotsPrice/parkingSpotsPriceTable";
import { getParkingSpots } from "../../api/locations";
import ButtonText from "../../components/common/buttonText";
import { changeParkingSpotPrice, deleteParkingSpotPrice } from "../../api/permits";

const ParkingSpotPricePage = () => {
    const { t } = useTranslation();
    const { showToast } = useMessages();
    const { parkingAreaExtId } = useParams();

    const [loading, setLoading] = useState(true);
    const [parkingSpots, setParkingSpots] = useState([]);

    const [parkingSpotForPrice, setParkingSpotForPrice] = useState(null);
    const [showEditPriceModal, setShowEditPriceModal] = useState(false);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [parkingSpotPendingDelete, setParkingSpotPendingDelete] = useState(null);

    const [price, setPrice] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [fromDateTime, setFromDateTime] = useState('');
    const [toDateTime, setToDateTime] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const token = await getToken();
                const result = await getParkingSpots(token, parkingAreaExtId);
                if (result && !result.error) {
                    setParkingSpots(result);
                } else {
                    console.error(result);
                    showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
                }
            } catch (e) {
                console.error(e);
                showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
            } finally {
                setLoading(false);
            }
        };
        if (parkingAreaExtId) load();
    }, [parkingAreaExtId, showToast, t]);

    useEffect(() => {
        if (showEditPriceModal) document.body.classList.add('overflow-hidden');
        else document.body.classList.remove('overflow-hidden');
        return () => document.body.classList.remove('overflow-hidden');
    }, [showEditPriceModal]);

    useEffect(() => {
        if (showEditPriceModal && parkingSpotForPrice) {
            setPrice(
                parkingSpotForPrice.price !== undefined && parkingSpotForPrice.price !== null
                    ? String(parkingSpotForPrice.price)
                    : null
            );
        } else {
            setPrice(null);
        }
    }, [showEditPriceModal, parkingSpotForPrice]);

    const savePrice = async () => {
        try {
            if (isProcessing) return;
            setIsProcessing(true);

            if (price === null || price === '') {
                showToast(t('generic.errorTitle'), t('parkingSpotsPricePage.priceRequired', { defaultValue: 'Price is required.' }), 'error');
                return;
            }
            const numericPrice = (price === null || price === '')
                ? null
                : Number(String(price).replace(',', '.'));

            if (numericPrice !== null && Number.isNaN(numericPrice)) {
                showToast(t('generic.errorTitle'), t('parkingSpotsPricePage.invalidPrice'), 'error');
                return;
            }

            if (numericPrice === 0) {
                showToast(t('generic.errorTitle'), t('parkingSpotsPricePage.priceMustBeGreaterThanZero', { defaultValue: 'Price must be greater than 0.' }), 'error');
                return;
            }

            const token = await getToken();

            // Bulk update if array, else single
            if (Array.isArray(parkingSpotForPrice)) {
                let anyError = false;
                for (const spot of parkingSpotForPrice) {
                    const resp = await changeParkingSpotPrice(token, {
                        parkingSpotId: spot.parkingSpotId,
                        price: numericPrice,
                    });
                    if (resp?.error) {
                        anyError = true;
                        console.error(resp);
                        showToast(t('generic.errorTitle'), resp?.message || t('generic.errorDescription'), 'error');
                    } else {
                        setParkingSpots(prev =>
                            prev.map(s =>
                                s.parkingSpotId === spot.parkingSpotId
                                    ? { ...s, price: numericPrice }
                                    : s
                            )
                        );
                    }
                }
                if (!anyError) {
                    showToast(
                        t('parkingSpotsPricePage.savedTitle'),
                        t('parkingSpotsPricePage.savedDesc'),
                        'success'
                    );
                }
            } else {
                if (!parkingSpotForPrice?.parkingSpotId) {
                    showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
                    return;
                }
                const resp = await changeParkingSpotPrice(token, {
                    parkingSpotId: parkingSpotForPrice.parkingSpotId,
                    price: numericPrice,
                });

                if (resp?.error) {
                    console.error(resp);
                    showToast(t('generic.errorTitle'), resp?.message || t('generic.errorDescription'), 'error');
                    return;
                }

                setParkingSpots(prev =>
                    prev.map(s =>
                        s.parkingSpotId === parkingSpotForPrice.parkingSpotId
                            ? { ...s, price: numericPrice }
                            : s
                    )
                );

                showToast(
                    t('parkingSpotsPricePage.savedTitle'),
                    t('parkingSpotsPricePage.savedDesc'),
                    'success'
                );
            }

            setShowEditPriceModal(false);
            setParkingSpotForPrice(null);
            setPrice(null);
        } catch (e) {
            console.error(e);
            showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    const deletePrice = async (spot) => {
        try {
            if (isProcessing) return;
            setIsProcessing(true);
            if (!spot?.parkingSpotId) {
                showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
                return;
            }

            const token = await getToken();
            const resp = await deleteParkingSpotPrice(token, { parkingSpotId: spot.parkingSpotId });

            if (resp?.error) {
                console.error(resp);
                showToast(t('generic.errorTitle'), resp?.message || t('generic.errorDescription'), 'error');
                return;
            }

            setParkingSpots(prev => prev.map(s => s.parkingSpotId === spot.parkingSpotId ? { ...s, price: null } : s));

            showToast(
                t('parkingSpotsPricePage.deletedTitle', { defaultValue: 'Price removed' }),
                t('parkingSpotsPricePage.deletedDesc', { defaultValue: 'Spot price has been removed.' }),
                'success'
            );
            setShowConfirmDeleteModal(false);
            setParkingSpotPendingDelete(null);
        } catch (e) {
            console.error(e);
            showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    const requestDeletePrice = (spot) => {
        setParkingSpotPendingDelete(spot);
        setShowConfirmDeleteModal(true);
    };

    return (
        <>
            <Widget>
                <div className="flex flex-col">
                    <h2 className="text-xl font-medium mb-3">
                        {t('parkingSpotsPricePage.title')}
                    </h2>
                    <span className="text-gray-600">
            {t('parkingSpotsPricePage.description')}
          </span>
                </div>
            </Widget>

            {!loading ? (
                <div className="mt-6">
                    <Widget>
                        <ParkingSpotsPriceTable
                            parkingSpots={parkingSpots}
                            setParkingSpotForPrice={setParkingSpotForPrice}
                            setShowEditPriceModal={setShowEditPriceModal}
                            onDeleteSpotPrice={requestDeletePrice}
                        />
                    </Widget>
                </div>
            ) : (
                <div className="mt-6">
                    <WidgetLoadingSkeleton />
                </div>
            )}

            {showConfirmDeleteModal && (
                <>
                    <div
                        className="fixed inset-0 z-50 bg-black bg-opacity-60"
                        onClick={() => {
                            if (isProcessing) return;
                            setShowConfirmDeleteModal(false);
                            setParkingSpotPendingDelete(null);
                        }}
                    />

                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center px-6"
                        onClick={() => {
                            if (isProcessing) return;
                            setShowConfirmDeleteModal(false);
                            setParkingSpotPendingDelete(null);
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            className="flex flex-col w-full bg-white max-w-md rounded-lg p-6"
                        >
                            <div className="flex justify-end">
                                <button
                                    className="h-4"
                                    onClick={() => {
                                        if (isProcessing) return;
                                        setShowConfirmDeleteModal(false);
                                        setParkingSpotPendingDelete(null);
                                    }}
                                    aria-label="Close"
                                    title="Close"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="flex justify-center mb-4">
                                <span className="font-medium">
                                    {t('parkingSpotsPricePage.confirmDeleteTitle', { defaultValue: 'Remove price?' })}
                                </span>
                            </div>
                            <div className="text-gray-700 mb-6 text-center">
                                {t('parkingSpotsPricePage.confirmDeleteDesc', { defaultValue: 'This will remove the price for the selected parking spot.' })}
                            </div>

                            <div className="flex gap-3">
                                <ButtonText
                                    isDelete={true}
                                    className="w-full"
                                    onClick={() => {
                                        if (parkingSpotPendingDelete) {
                                            deletePrice(parkingSpotPendingDelete);
                                        }
                                    }}
                                    isLoading={isProcessing}
                                    disabled={isProcessing}
                                >
                                    {t('generic.delete', { defaultValue: 'Delete' })}
                                </ButtonText>
                                <ButtonText
                                    className="w-full"
                                    onClick={() => {
                                        setShowConfirmDeleteModal(false);
                                        setParkingSpotPendingDelete(null);
                                    }}
                                    disabled={isProcessing}
                                >
                                    {t('generic.cancel', { defaultValue: 'Cancel' })}
                                </ButtonText>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {showEditPriceModal && (
                <>
                    <div
                        className="fixed inset-0 z-50 bg-black bg-opacity-60"
                        onClick={() => {
                            setShowEditPriceModal(false);
                            setParkingSpotForPrice(null);
                            setPrice(null);
                        }}
                    />

                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center px-6"
                        onClick={() => {
                            setShowEditPriceModal(false);
                            setParkingSpotForPrice(null);
                            setPrice(null);
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            className="flex flex-col w-full bg-white max-w-3xl rounded-lg p-6"
                        >
                            <div className="flex justify-end">
                                <button
                                    className="h-4"
                                    onClick={() => {
                                        setShowEditPriceModal(false);
                                        setParkingSpotForPrice(null);
                                        setPrice(null);
                                    }}
                                    aria-label="Close"
                                    title="Close"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="flex justify-center mb-6">
                <span className="font-medium">
                  {t('parkingSpotsPricePage.editPriceTitle')}
                </span>
                            </div>

                            {parkingSpotForPrice && (
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-1">
                                        {t('parkingSpotsPricePage.spotId')}
                                    </label>
                                    {Array.isArray(parkingSpotForPrice) ? (
                                        <div className="flex flex-wrap gap-2">
                                            {parkingSpotForPrice.map((spot, idx) => (
                                                <span key={idx} className="px-2 py-1 bg-gray-100 rounded text-xs">
                                                    {spot.parkingSpotExtId}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <input
                                            type="text"
                                            className="w-full rounded-md border px-3 py-2 bg-gray-50 text-gray-900 cursor-default"
                                            readOnly
                                            value={parkingSpotForPrice.parkingSpotExtId}
                                        />
                                    )}
                                </div>
                            )}

                            <div className="mb-6">
                                <label className="block text-sm font-medium mb-1">
                                    {t('parkingSpotsPricePage.price')}
                                </label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    pattern="[0-9]*[.,]?[0-9]*"
                                    className="w-full rounded-md border px-3 py-2"
                                    value={price ?? ''}
                                    onChange={(e) => {
                                        const v = e.target.value
                                            .replace(',', '.')
                                            .replace(/[^0-9.]/g, '');
                                        setPrice(v === '' ? null : v);
                                    }}
                                />
                            </div>

                            <div className="flex">
                                <ButtonText onClick={savePrice} className="w-full" isLoading={isProcessing} disabled={isProcessing || price === null || price === '' || price === '0'}>
                                    {t('generic.save', { defaultValue: 'Save' })}
                                </ButtonText>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default ParkingSpotPricePage;
